<script setup lang="ts">
// The same reading per species: females per analysed sample for the six species
// with the largest female catch this season, each against its own five-season
// band rather than the total's. One card per species, full rail width, because
// six charts sharing a grid at rail width would be six unreadable cells.
import { computed } from "vue";

import type {
	MapReviewPopulationSpeciesTrend,
	MapReviewPopulationSurveillance,
	MapReviewSurveillanceWeek,
} from "@/type/api";

import BaselineChart from "../charts/BaselineChart.vue";
import { OKABE_ITO } from "../charts/theme";

const props = defineProps<{
	surveillance: MapReviewPopulationSurveillance;
}>();

const GOOD = OKABE_ITO[2];
const BAD = OKABE_ITO[1];
const NEUTRAL = OKABE_ITO[0];

/** The five prior seasons, for the band's legend name. */
const priorYears = computed(() =>
	props.surveillance.prior.map((row) => row.year),
);

/** A species' chart series, remapped onto the band chart's week shape. The
 * current line, its band's mean and the band's edges are all aligned by index
 * on the surveillance week axis, so a missing week stays a gap. */
function weeksFor(
	species: MapReviewPopulationSpeciesTrend,
): MapReviewSurveillanceWeek[] {
	return props.surveillance.weeks.map((week, index) => ({
		week,
		value: species.current[index] ?? null,
		baseline_mean: species.band.mean[index] ?? null,
		baseline_min: species.band.lo[index] ?? null,
		baseline_max: species.band.hi[index] ?? null,
		samples: 0,
	}));
}

/** The badge's colour, and the line's: the two say the same thing. Falling is
 * good, rising is bad, flat is neither. */
function lineColor(slope: number | null): string {
	if (slope === null) return NEUTRAL;
	if (slope > 2) return BAD;
	if (slope < -2) return GOOD;
	return NEUTRAL;
}

function badge(slope: number | null): string {
	if (slope === null) return "no slope";
	return `${slope > 0 ? "+" : ""}${slope.toFixed(1)}%/wk`;
}

function ariaLabel(species: MapReviewPopulationSpeciesTrend): string {
	const years = priorYears.value;
	const history =
		years.length === 0
			? "its prior seasons"
			: `${years[0]}–${years[years.length - 1]}`;
	return `${species.name} females per analysed sample against ${history}`;
}
</script>

<template>
	<section class="population-species">
		<p class="panel-title">By species</p>
		<p class="panel-help">
			Females per analysed sample, each species against its own five-season
			band. The badge is that species' trailing six-week slope; the line's
			colour follows the badge. A species filter picks the cards; otherwise the
			six largest female catches are shown. A sex filter does not narrow this
			panel: its metric is females per analysed sample by definition.
		</p>
		<p v-if="surveillance.species.length === 0" class="panel-empty">
			No species was caught under this filter.
		</p>
		<article
			v-for="species in surveillance.species"
			:key="species.id"
			class="species-card"
		>
			<header class="species-head">
				<p class="species-name">{{ species.name }}</p>
				<span class="badge" :style="{ color: lineColor(species.slope) }">{{
					badge(species.slope)
				}}</span>
			</header>
			<div class="species-chart">
				<BaselineChart
					:weeks="weeksFor(species)"
					:label="species.name"
					:baseline-years="priorYears"
					week-label=""
					:ariaLabel="ariaLabel(species)"
					compact
					:line-color="lineColor(species.slope)"
				/>
			</div>
		</article>
	</section>
</template>

<style scoped>
.population-species {
	background: #fff;
	border: 1px solid #dee2e6;
	border-radius: 4px;
	padding: 8px 10px 10px;
	min-width: 0;
	/* The pane's slot is a cell grid; this panel reads as one column of the
	   rail, in the order the question asks. */
	grid-column: 1 / -1;
}

.panel-title {
	margin: 0 0 2px;
	font-size: 0.78rem;
	font-weight: 600;
	color: #212529;
}

.panel-help {
	margin: 0 0 8px;
	font-size: 0.68rem;
	line-height: 1.35;
	color: #6c757d;
}

.panel-empty {
	margin: 0;
	font-size: 0.72rem;
	color: #6c757d;
}

.species-card {
	border-top: 1px solid #e9ecef;
	padding-top: 6px;
	margin-top: 6px;
	min-width: 0;
}

.species-card:first-of-type {
	border-top: 0;
	padding-top: 0;
	margin-top: 0;
}

.species-head {
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: 8px;
	margin-bottom: 2px;
}

.species-name {
	margin: 0;
	font-size: 0.72rem;
	font-weight: 600;
	color: #343a40;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.badge {
	flex: 0 0 auto;
	font-size: 0.68rem;
	font-variant-numeric: tabular-nums;
	font-weight: 600;
}

.species-chart {
	position: relative;
	height: 126px;
}
</style>
